import { Box, Checkbox, Divider, Group, Paper, Table, Text } from '@mantine/core';

import { PLATFORMS, SECTIONS, type PlatformKey } from '../constants/pageAccess';
import { roleTypes } from '../types';
import { PAGE_ACCESS_SECTION_ICONS, PLATFORM_ICONS } from '../utils/icons';
import { isAllSelected, isAnySelected } from '../utils/pageAccess';

type Props = {
  section: (typeof SECTIONS)[number];
  getSelected: (
    section: keyof roleTypes.PageAccessConfig,
    platform: PlatformKey
  ) => roleTypes.PageAccessDepartment[];
  onToggleSection: () => void;
  onTogglePlatform: (platform: PlatformKey) => void;
  onToggle: (platform: PlatformKey, department: roleTypes.PageAccessDepartment) => void;
};

export const PageAccessSectionCard = ({
  section,
  getSelected,
  onToggleSection,
  onTogglePlatform,
  onToggle,
}: Props) => {
  const sectionFull = PLATFORMS.every((platform) =>
    isAllSelected(getSelected(section.key, platform.key), section.departments)
  );
  const sectionPartial = PLATFORMS.some((platform) =>
    isAnySelected(getSelected(section.key, platform.key), section.departments)
  );
  const columns = section.departments.map((department) => ({
    key: department,
    label: department === 'unassigned' ? 'Unassigned' : department.toUpperCase(),
  }));

  return (
    <Paper withBorder p="sm" radius="sm">
      <Checkbox
        mb="sm"
        label={
          <Group gap={6} align="center">
            {PAGE_ACCESS_SECTION_ICONS[section.label] && (
              <Box style={{ opacity: 0.5, display: 'flex' }}>
                {PAGE_ACCESS_SECTION_ICONS[section.label](16)}
              </Box>
            )}
            <Text size="sm" fw={600}>
              {section.label}
            </Text>
          </Group>
        }
        checked={sectionFull}
        indeterminate={!sectionFull && sectionPartial}
        onChange={onToggleSection}
      />
      <Divider mb="sm" />
      <Table withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={150} />
            {columns.map((column) => (
              <Table.Th key={column.key} ta="center">
                {column.label}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {PLATFORMS.map((platform) => {
            const selected = getSelected(section.key, platform.key);
            return (
              <Table.Tr key={platform.key}>
                <Table.Td>
                  <Group gap="xs" justify="space-between">
                    <Group gap={8} align="center">
                      <Box style={{ opacity: 0.5, display: 'flex' }}>
                        {PLATFORM_ICONS[platform.key](16)}
                      </Box>
                      <Text size="sm">{platform.label}</Text>
                    </Group>
                    <Checkbox
                      label="All"
                      aria-label={`All — ${section.label} / ${platform.label}`}
                      checked={isAllSelected(selected, section.departments)}
                      indeterminate={
                        !isAllSelected(selected, section.departments) &&
                        isAnySelected(selected, section.departments)
                      }
                      onChange={() => onTogglePlatform(platform.key)}
                    />
                  </Group>
                </Table.Td>
                {columns.map((column) => (
                  <Table.Td key={column.key} ta="center">
                    <Checkbox
                      aria-label={`${section.label} / ${platform.label} / ${column.label}`}
                      checked={selected.includes(column.key)}
                      onChange={() => onToggle(platform.key, column.key)}
                    />
                  </Table.Td>
                ))}
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Paper>
  );
};
